import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import {
  getScreenings,
  getScreeningStats,
  createScreening,
  updateScreening,
  completeScreening,
  deleteScreening,
  getScreeningStatus,
  getPriorityInfo,
  getCategoryInfo,
  screeningTypes,
  screeningCategories,
  frequencyOptions,
  reminderOptions,
  type Screening as ScreeningType,
  type ScreeningStats,
  type ScreeningCreateData,
} from '../api/screening';

type FilterType = 'pending' | 'completed' | 'all';

// Date utilities for Italian format
function isoToItalian(isoDate: string): string {
  if (!isoDate) return '';
  const parts = isoDate.split('T')[0].split('-');
  if (parts.length !== 3) return isoDate;
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

function italianToIso(italianDate: string): string {
  if (!italianDate) return '';
  const parts = italianDate.split('/');
  if (parts.length !== 3) return italianDate;
  const [day, month, year] = parts;
  if (day.length === 2 && month.length === 2 && year.length === 4) {
    return `${year}-${month}-${day}`;
  }
  return italianDate;
}

function getTodayItalian(): string {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${day}/${month}/${year}`;
}

function formatDateLong(dateString: string): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('it-IT', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// Italian date input component
function ItalianDateInput({
  value,
  onChange,
  placeholder = 'gg/mm/aaaa',
  className = '',
  required = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
}) {
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^\d/]/g, '');
    if (val.length === 2 && !val.includes('/')) val += '/';
    if (val.length === 5 && val.split('/').length === 2) val += '/';
    if (val.length <= 10) onChange(val);
  };

  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      onChange(isoToItalian(e.target.value));
    }
  };

  const isoValue = italianToIso(value);

  return (
    <div className="relative flex items-center">
      <input
        type="text"
        value={value}
        onChange={handleTextChange}
        placeholder={placeholder}
        required={required}
        className={`${className} pr-10`}
      />
      <div className="absolute right-2 flex items-center">
        <input
          type="date"
          value={isoValue}
          onChange={handleDatePickerChange}
          className="w-6 h-6 cursor-pointer"
          style={{ colorScheme: 'light' }}
        />
      </div>
    </div>
  );
}

export function Screening() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [screenings, setScreenings] = useState<ScreeningType[]>([]);
  const [stats, setStats] = useState<ScreeningStats | null>(null);
  const [filter, setFilter] = useState<FilterType>('pending');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingScreening, setEditingScreening] = useState<ScreeningType | null>(null);
  const [selectedTypeValue, setSelectedTypeValue] = useState<string>('');
  const [formData, setFormData] = useState<{
    name: string;
    screeningType: string;
    recommendedDate: string;
    frequency: string;
    reminderDays: number;
    priority: string;
    notes: string;
    emailReminderEnabled: boolean;
  }>({
    name: '',
    screeningType: 'generale',
    recommendedDate: getTodayItalian(),
    frequency: '12 mesi',
    reminderDays: 30,
    priority: 'normal',
    notes: '',
    emailReminderEnabled: true,
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [screeningsRes, statsRes] = await Promise.all([
        getScreenings(),
        getScreeningStats(),
      ]);
      if (screeningsRes.success) {
        setScreenings(screeningsRes.screenings);
      }
      if (statsRes.success) {
        setStats(statsRes.stats);
      }
    } catch (err) {
      setError('Errore nel caricamento degli screening');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredScreenings = screenings.filter((s) => {
    if (filter === 'pending') return !s.completed;
    if (filter === 'completed') return s.completed;
    return true;
  });

  const handleOpenForm = (screening?: ScreeningType) => {
    if (screening) {
      setEditingScreening(screening);
      // Find matching screening type by name
      const matchingType = screeningTypes.find(t => t.name === screening.name);
      setSelectedTypeValue(matchingType ? matchingType.value : 'altro');
      setFormData({
        name: screening.name,
        screeningType: screening.screeningType,
        recommendedDate: isoToItalian(screening.recommendedDate),
        frequency: screening.frequency || '',
        reminderDays: screening.reminderDays,
        priority: screening.priority,
        notes: screening.notes || '',
        emailReminderEnabled: screening.emailReminderEnabled ?? true,
      });
    } else {
      setEditingScreening(null);
      setSelectedTypeValue('');
      setFormData({
        name: '',
        screeningType: 'generale',
        recommendedDate: getTodayItalian(),
        frequency: '12 mesi',
        reminderDays: 30,
        priority: 'normal',
        notes: '',
        emailReminderEnabled: true,
      });
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingScreening(null);
    setSelectedTypeValue('');
  };

  const handleTypeChange = (typeValue: string) => {
    setSelectedTypeValue(typeValue);
    const selectedType = screeningTypes.find(t => t.value === typeValue);
    if (selectedType) {
      if (typeValue === 'altro') {
        // For "Altro", keep empty name and set category to generale
        setFormData({
          ...formData,
          name: '',
          screeningType: 'generale',
        });
      } else {
        // Auto-fill name and category from selected type
        setFormData({
          ...formData,
          name: selectedType.name,
          screeningType: selectedType.category,
        });
      }
    }
  };

  const handleSave = async () => {
    if (!selectedTypeValue || !formData.name || !formData.recommendedDate) return;

    try {
      const data: ScreeningCreateData = {
        name: formData.name,
        screeningType: formData.screeningType,
        recommendedDate: italianToIso(formData.recommendedDate),
        frequency: formData.frequency || undefined,
        reminderDays: formData.reminderDays,
        priority: formData.priority,
        notes: formData.notes || undefined,
        emailReminderEnabled: formData.emailReminderEnabled,
      };

      if (editingScreening) {
        await updateScreening(editingScreening.id, data);
        setSuccess('Screening aggiornato');
      } else {
        await createScreening(data);
        setSuccess('Screening creato');
      }

      handleCloseForm();
      loadData();
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Errore nel salvataggio');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleComplete = async (screening: ScreeningType) => {
    try {
      await completeScreening(screening.id);
      setSuccess('Screening completato');
      loadData();
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Errore nel completamento');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Sei sicuro di voler eliminare questo screening?')) return;

    try {
      await deleteScreening(id);
      setSuccess('Screening eliminato');
      loadData();
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Errore nell\'eliminazione');
      setTimeout(() => setError(null), 3000);
    }
  };

  if (!user) return null;

  return (
    <Layout title="Screening">
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">📅</span>
            <h1 className="text-2xl font-bold text-gray-900">Screening e Prevenzione</h1>
          </div>
          <p className="text-gray-600">
            Gestisci i tuoi screening programmati e tieni traccia della prevenzione
          </p>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 text-xl">📆</span>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.scheduled}</div>
                <div className="text-sm text-gray-500">In programma</div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <span className="text-amber-600 text-xl">⏰</span>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.nextThirtyDays}</div>
                <div className="text-sm text-gray-500">Prossimi 30 giorni</div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <span className="text-red-600 text-xl">⚠️</span>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.overdue}</div>
                <div className="text-sm text-gray-500">Scaduti</div>
              </div>
            </div>
          </div>
        )}

        {/* Screening List */}
        <div className="bg-white rounded-xl border border-gray-200">
          {/* List Header */}
          <div className="p-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-gray-900">Elenco Screening</h2>
            <div className="flex items-center gap-3">
              {/* Filter Tabs */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setFilter('pending')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    filter === 'pending'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  In attesa
                </button>
                <button
                  onClick={() => setFilter('completed')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    filter === 'completed'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Completati
                </button>
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    filter === 'all'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Tutti
                </button>
              </div>
              {/* Add Button */}
              <button
                onClick={() => handleOpenForm()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <span>+</span>
                <span>Nuovo Screening</span>
              </button>
            </div>
          </div>

          {/* List Content */}
          <div className="divide-y divide-gray-100">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">Caricamento...</div>
            ) : filteredScreenings.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {filter === 'pending'
                  ? 'Nessuno screening in attesa'
                  : filter === 'completed'
                  ? 'Nessuno screening completato'
                  : 'Nessuno screening presente'}
              </div>
            ) : (
              filteredScreenings.map((screening) => {
                const status = getScreeningStatus(screening);
                const priority = getPriorityInfo(screening.priority);
                const category = getCategoryInfo(screening.screeningType);

                return (
                  <div key={screening.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Badges */}
                        <div className="flex flex-wrap gap-2 mb-2">
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.bgColor} ${status.color}`}
                          >
                            {status.label}
                          </span>
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${category.bgColor} ${category.color}`}
                          >
                            {category.label}
                          </span>
                          {screening.priority !== 'normal' && (
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded-full ${priority.bgColor} ${priority.color}`}
                            >
                              {priority.label}
                            </span>
                          )}
                        </div>
                        {/* Title */}
                        <h3 className="text-base font-medium text-gray-900 mb-1">
                          {screening.name}
                        </h3>
                        {/* Details */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <span>📅</span>
                            <span>
                              {screening.completed
                                ? `Completato il ${formatDateLong(screening.completedAt!)}`
                                : `Previsto per il ${formatDateLong(screening.recommendedDate)}`}
                            </span>
                          </span>
                          {screening.frequency && (
                            <span className="flex items-center gap-1">
                              <span>🔄</span>
                              <span>Ogni {screening.frequency}</span>
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <span>🔔</span>
                            <span>Promemoria {screening.reminderDays}gg prima</span>
                          </span>
                        </div>
                        {screening.notes && (
                          <p className="mt-2 text-sm text-gray-600">{screening.notes}</p>
                        )}
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-2 ml-4">
                        {!screening.completed && (
                          <button
                            onClick={() => handleComplete(screening)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Segna come completato"
                          >
                            ✓
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenForm(screening)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="Modifica"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(screening.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Elimina"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingScreening ? 'Modifica Screening' : 'Nuovo Screening'}
                </h3>
              </div>
              <div className="p-6 space-y-4">
                {/* Screening Type Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo Screening *
                  </label>
                  <select
                    value={selectedTypeValue}
                    onChange={(e) => handleTypeChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Seleziona un tipo...</option>
                    {screeningTypes.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Custom Name and Category (only for "Altro") */}
                {selectedTypeValue === 'altro' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nome screening *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Inserisci il nome dello screening..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Categoria
                      </label>
                      <select
                        value={formData.screeningType}
                        onChange={(e) => setFormData({ ...formData, screeningType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {screeningCategories.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data prevista *
                  </label>
                  <ItalianDateInput
                    value={formData.recommendedDate}
                    onChange={(val) => setFormData({ ...formData, recommendedDate: val })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frequenza
                  </label>
                  <select
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Nessuna (una tantum)</option>
                    {frequencyOptions.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Reminder */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Promemoria
                  </label>
                  <select
                    value={formData.reminderDays}
                    onChange={(e) =>
                      setFormData({ ...formData, reminderDays: parseInt(e.target.value) })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {reminderOptions.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Email Reminder Toggle */}
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm text-gray-700">Promemoria via Email</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, emailReminderEnabled: !formData.emailReminderEnabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.emailReminderEnabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.emailReminderEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priorità
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="normal">Normale</option>
                    <option value="alta">Alta</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Note aggiuntive..."
                  />
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex gap-3">
                <button
                  onClick={handleSave}
                  disabled={!selectedTypeValue || !formData.name || !formData.recommendedDate}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {editingScreening ? 'Salva Modifiche' : 'Crea Screening'}
                </button>
                <button
                  onClick={handleCloseForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annulla
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
